import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getLatestYoutubeVideos,
  type YoutubeVideo,
} from '../../../../services/api/youtubeVideos';

import { AdManagerSlot } from '../../../../components/AdManagerSlot';

import './VideoCastView.less';

function formatDate(date?: string) {
  if (!date) return '';

  if (date.includes('|')) {
    return date;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

export default function VideoCastView() {
  const [videos, setVideos] = useState<YoutubeVideo[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const featuredVideo = useMemo(() => videos[0], [videos]);
  const remainingVideos = useMemo(() => videos.slice(1), [videos]);

  const loadVideos = async (targetPage = 0, append = false) => {
    try {
      append ? setIsLoadingMore(true) : setLoading(true);
      setError(false);

      const data = await getLatestYoutubeVideos(targetPage);

      setHasMore(data.length > 0);

      setVideos((current) => {
        if (!append) return data;

        const existingIds = new Set(current.map((video) => video.id));
        const nextVideos = data.filter((video) => !existingIds.has(video.id));

        return [...current, ...nextVideos];
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    async function loadInitialVideos() {
      try {
        setLoading(true);
        setError(false);

        const data = await getLatestYoutubeVideos(0);

        if (isMounted) {
          setVideos(data);
          setHasMore(data.length > 0);
          setPage(0);
        }
      } catch {
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadInitialVideos();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLoadMore = () => {
    const nextPage = page + 1;

    setPage(nextPage);
    loadVideos(nextPage, true);
  };

  if (loading) {
    return (
      <main className="videocast-view">
        <section className="videocast-view__state">
          <p>Carregando vídeos...</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="videocast-view">
        <section className="videocast-view__state">
          <p>Não foi possível carregar os vídeos no momento.</p>
        </section>
      </main>
    );
  }

  if (!featuredVideo) {
    return (
      <main className="videocast-view">
        <section className="videocast-view__state">
          <p>Nenhum vídeo encontrado.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="videocast-view">
      <section className="videocast-view__hero">
        <div className="videocast-view__hero-content">
          <div className="videocast-view__eyebrow">
            Videocast Rádio Nacional
          </div>

          <h1>
            Vídeos, entrevistas e apresentações da <span>Rádio Nacional</span>
          </h1>

          <p>
            Acompanhe conteúdos audiovisuais, conversas, coberturas especiais e
            registros em vídeo publicados no canal oficial da Rádio Nacional.
          </p>
        </div>

        <article className="videocast-view__featured">
          <Link
            to={`/videocasts/${featuredVideo.id}`}
            className="videocast-view__featured-thumb"
            aria-label={`Assistir ${featuredVideo.title}`}
          >
            <img src={featuredVideo.thumbnail} alt="" loading="eager" />

            <div className="videocast-view__featured-overlay" />

            <span className="videocast-view__play">Assistir</span>
          </Link>

          <div className="videocast-view__featured-content">
            <div className="videocast-view__label">Mais recente</div>

            {featuredVideo.publishedAt && (
              <div className="videocast-view__date">
                {formatDate(featuredVideo.publishedAt)}
              </div>
            )}

            <h2>
              <Link to={`/videocasts/${featuredVideo.id}`}>
                {featuredVideo.title}
              </Link>
            </h2>

            {featuredVideo.description && <p>{featuredVideo.description}</p>}
          </div>
        </article>
      </section>

      <AdManagerSlot
        slotKey="largeLeaderboard"
        className="ad-manager-slot--leaderboard"
      />

      {remainingVideos.length > 0 && (
        <section className="videocast-view__grid-section">
          <h2>Últimos vídeos</h2>

          <div className="videocast-view__grid">
            {remainingVideos.map((video) => (
              <article className="videocast-view__card" key={video.id}>
                <Link
                  to={`/videocasts/${video.id}`}
                  className="videocast-view__card-media"
                  aria-label={`Assistir ${video.title}`}
                >
                  <img src={video.thumbnail} alt="" loading="lazy" />
                  <span>Assistir</span>
                </Link>

                <div className="videocast-view__card-content">
                  {video.publishedAt && (
                    <time>{formatDate(video.publishedAt)}</time>
                  )}

                  <h3>
                    <Link to={`/videocasts/${video.id}`}>{video.title}</Link>
                  </h3>

                  {video.description && <p>{video.description}</p>}
                </div>
              </article>
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              className="videocast-view__load-more"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Carregando...' : 'Carregar mais vídeos'}
            </button>
          )}
        </section>
      )}
    </main>
  );
}
